import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { clientPortalApi } from '../lib/api';
import { formatDate, formatDateTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { File, Download, Lock, ChatCircle, PaperPlaneTilt, Buildings, UserCircle, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function ClientPortalView() {
  const { token } = useParams();
  const [summary, setSummary] = useState(null);
  const [dealData, setDealData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const sumRes = await clientPortalApi.view(token);
      setSummary(sumRes.data);
      const dealId = sumRes.data.deal_id;
      const [dealRes, docsRes] = await Promise.all([
        clientPortalApi.viewDeal(token, dealId),
        clientPortalApi.viewDocs(token, dealId),
      ]);
      setDealData(dealRes.data);
      setDocuments(docsRes.data);
      if (sumRes.data.access_level === 'interactive') {
        try {
          const msgRes = await clientPortalApi.getMessages(token);
          setMessages(msgRes.data);
        } catch { /* ignore */ }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to access portal');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleDownload = async (doc) => {
    if (doc.restrict_download) {
      toast.error('Download is restricted for this document');
      return;
    }
    try {
      const res = await clientPortalApi.downloadDoc(token, doc.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename || doc.name;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Download failed');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim()) return;
    try {
      const res = await clientPortalApi.postMessage(token, { message: newMsg.trim() });
      setMessages([...messages, res.data]);
      setNewMsg('');
      toast.success('Message sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md w-full bg-card border border-border rounded-xl p-8 text-center" data-testid="portal-error">
          <Warning size={48} className="mx-auto text-destructive mb-4" />
          <h1 className="font-heading text-xl font-bold mb-2">Access Denied</h1>
          <p className="text-muted-foreground text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const brand = summary?.brand || {};
  const deal = dealData?.deal || {};
  const interactive = summary?.access_level === 'interactive';

  return (
    <div className="min-h-screen bg-background" data-testid="portal-view">
      {/* Branded header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {brand.logo_url && (
              <img src={brand.logo_url.startsWith('http') ? brand.logo_url : `${process.env.REACT_APP_BACKEND_URL}/api/documents/${brand.logo_url}/download`} alt="Logo" className="h-8 w-8 object-contain rounded-sm" />
            )}
            <div>
              <h1 className="font-heading text-lg font-bold">{brand.app_name || 'Client Portal'}</h1>
              <p className="text-xs text-muted-foreground">Secure deal review</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Welcome</p>
            <p className="text-sm font-medium">{summary?.guest_name || 'Guest'}</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6">
        {/* Access banner */}
        <div className="bg-card border border-border rounded-xl p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {interactive ? <ChatCircle size={24} className="text-emerald-500" /> : <Lock size={24} className="text-blue-500" />}
            <div>
              <p className="text-sm font-medium">{interactive ? 'Interactive access' : 'Read-only access'}</p>
              <p className="text-xs text-muted-foreground">
                {interactive ? 'You can view the deal, documents and send messages.' : 'You can view the deal and download available documents.'}
              </p>
            </div>
          </div>
          {summary?.expires_at && (
            <Badge variant="outline" className="text-xs">Expires {formatDate(summary.expires_at)}</Badge>
          )}
        </div>

        {/* Deal summary */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h2 className="font-heading text-2xl font-bold mb-2">{deal.title || summary?.deal_title}</h2>
          {deal.description && <p className="text-sm text-muted-foreground mb-4">{deal.description}</p>}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
            <Stat label="Value" value={deal.value ? `$${Number(deal.value).toLocaleString()}` : '—'} />
            <Stat label="Stage" value={deal.stage || '—'} />
            <Stat label="Probability" value={deal.probability ? `${deal.probability}%` : '—'} />
            <Stat label="Expected Close" value={deal.expected_close_date ? formatDate(deal.expected_close_date) : '—'} />
          </div>

          {(dealData?.company || dealData?.contact) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 pt-6 border-t border-border">
              {dealData.company && (
                <div className="flex items-start gap-3">
                  <Buildings size={20} className="text-primary mt-0.5" />
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Company</p>
                    <p className="text-sm font-medium">{dealData.company.name}</p>
                    {dealData.company.industry && <p className="text-xs text-muted-foreground">{dealData.company.industry}</p>}
                  </div>
                </div>
              )}
              {dealData.contact && (
                <div className="flex items-start gap-3">
                  <UserCircle size={20} className="text-primary mt-0.5" />
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Primary Contact</p>
                    <p className="text-sm font-medium">{dealData.contact.first_name} {dealData.contact.last_name}</p>
                    {dealData.contact.email && <p className="text-xs text-muted-foreground">{dealData.contact.email}</p>}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Documents */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="p-4 border-b border-border">
            <h3 className="font-heading text-lg font-semibold">Documents</h3>
            <p className="text-xs text-muted-foreground">Documents shared on this deal</p>
          </div>
          {documents.length === 0 ? (
            <p className="text-center text-muted-foreground text-sm p-8">No documents available</p>
          ) : (
            <div className="divide-y divide-border">
              {documents.map(doc => (
                <div key={doc.id} className="p-4 flex items-center justify-between gap-4 hover:bg-muted/30" data-testid={`doc-${doc.id}`}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <File size={20} className="text-primary flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{doc.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{doc.original_filename}</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" disabled={doc.restrict_download} onClick={() => handleDownload(doc)} data-testid={`dl-${doc.id}`}>
                    {doc.restrict_download ? <Lock size={14} className="mr-1" /> : <Download size={14} className="mr-1" />}
                    {doc.restrict_download ? 'Restricted' : 'Download'}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Messages (interactive only) */}
        {interactive && (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="p-4 border-b border-border">
              <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><ChatCircle size={18} /> Messages</h3>
              <p className="text-xs text-muted-foreground">Chat directly with the deal team</p>
            </div>
            <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
              {messages.length === 0 ? (
                <p className="text-center text-muted-foreground text-sm py-6">No messages yet. Say hi 👋</p>
              ) : messages.map(m => (
                <div key={m.id} className={`flex ${m.from_guest ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${m.from_guest ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                    <p className="text-sm">{m.message}</p>
                    <p className={`text-xs mt-1 ${m.from_guest ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>{formatDateTime(m.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={sendMessage} className="p-4 border-t border-border flex gap-2">
              <Input placeholder="Type your message…" value={newMsg} onChange={(e) => setNewMsg(e.target.value)} data-testid="msg-input" />
              <Button type="submit" data-testid="msg-send"><PaperPlaneTilt size={16} /></Button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">{label}</p>
      <p className="text-sm font-medium mt-1">{value}</p>
    </div>
  );
}
